import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "flowbite-react";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechaFormatosGeneralesInterface } from "../../../../../interfaces/FechaFormatosGeneralesInterface";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import FooterAutorizacionVD from "../../../../../img/Footer_AutorizacionVD.png";
import { ProtestaFotocopiasListaPrint } from "./ProtestaFotocopiasListaPrint";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const ProtestaFotocopiasListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "PROTESTA PARA FOTOCOPIAS";
  const [formData, setFormData] = useState({
    day: "",
    month: "",
    year: "",
  });

  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { reset } = useForm();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFechaFG = useGetData<FechaFormatosGeneralesInterface>({
    url: `/fecha_formatosgenerales/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "fecha_formatosgenerales"],
  });

  const navigate = useNavigate();

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  useEffect(() => {
    if (queryFechaFG.data) {
      const fechafg = queryFechaFG.data;
      reset({
        fecha_protesta_fotocopias: fechafg.fecha_protesta_fotocopias || "",
      });
    }
  }, [queryFechaFG.data, reset]);

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`max-w-4xl mx-auto shadow-lg rounded-lg overflow-hidden mt-4 mb-4 relative ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        {/* Header */}
        <Header titulo={tituloFormulario} />
        {/* Contenido del formulario */}
        <div className="p-6 flex flex-col gap-6">
          {/* Texto principal */}
          <p className={`text-justify leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
            Bajo protesta de decir verdad, hago constar que la presente fotocopia
            fue tomada del documento original, mismo que cuenta con validez
            oficial y fue expedido por una institución con reconocimiento
            gubernamental.
          </p>

          <p className={`text-justify leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
            Hago entrega a la empresa{" "}
            <strong>SKA TRACKING AND SECURITY S.A. de C.V.</strong> en la fecha:{" "}
            {queryFechaFG.data?.fecha_protesta_fotocopias
              ? (() => {
                  const date = new Date(
                    queryFechaFG.data.fecha_protesta_fotocopias
                  );
                  date.setDate(date.getDate() + 1);
                  return date.toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  });
                })()
              : "Fecha no disponible"}
          </p>

          {/* Datos del Evaluado */}
          <div className={`space-y-1 text-left ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            <h2 className="text-lg font-bold">{`NOMBRE: ${
              queryEvaluado.data?.nombre || ""
            } ${queryEvaluado.data?.apellido_paterno || ""} ${
              queryEvaluado.data?.apellido_materno || ""
            }`}</h2>
            <h2 className="text-lg font-bold">{`EMPRESA: ${queryEvaluado.data?.empresa_id.nombre}`}</h2>
            <h2 className="text-lg font-bold">{`PUESTO: ${
              queryEvaluado.data?.puesto || ""
            }`}</h2>
          </div>
        </div>

        {/* Firma */}
        <div className="pt-16 text-center">
          <div className={`w-64 mx-auto border-t pt-2 ${isDarkMode ? "border-gray-400" : "border-black"}`}>
            <p className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>FIRMA</p>
          </div>
        </div>

        {/* Botones */}
        <div className="mt-4 mb-4 px-4">
          <div className="flex justify-between">
            <Button
              color="red"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(-1);
              }}
              className={`${isDarkMode ? "bg-red-600 hover:bg-red-700" : "bg-red-500 hover:bg-red-600 text-white"}`}
            >
              Regresar
            </Button>
            <Button
              color="blue"
              className={`z-10 ${isDarkMode ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              onClick={handlePrint}
            >
              Imprimir
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className={`h-12 mt-8 flex items-center justify-center ${isDarkMode ? "bg-blue-800" : "bg-customBlue"}`}>
          <p className="text-sm text-white px-4">
            <span className="font-bold">DOCUMENTO CONFIDENCIAL</span>, prohibido
            su reproducción, copia o sacar este documento de las Instalaciones de
            Skyangel
          </p>
        </div>
        {/* Imprimir */}
        <div id="printableContent" style={{ display: "none" }}>
          <ProtestaFotocopiasListaPrint />
        </div>
      </div>
    </div>
  );
};